import { Suspense, useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { TITULOS } from "./visual.jsx";
import { colunasDaMesa } from "./casca.js";
import { useAcoes, useCasoAtual, useCelular, useEstado, useJanela } from "./estado.jsx";
import { useNavegacao } from "./navegacao.jsx";
import Entrada from "./componentes/Entrada.jsx";
import TrocaDeSenha from "./componentes/TrocaDeSenha.jsx";
import {
  BarraInferior,
  BarraSuperior,
  LARGURA_MINIMA_PARA_ABRIR,
  TRILHO_ABERTO,
  TRILHO_FECHADO,
  Trilho,
  lerTrilhoAberto,
  salvarTrilhoAberto,
} from "./componentes/Navegacao.jsx";
import Lista from "./componentes/Lista.jsx";
import { Esqueleto } from "./componentes/comuns.jsx";
import Painel from "./componentes/Painel.jsx";
import TelaPeticao from "./sobreposicoes/TelaPeticao.jsx";
import Etiquetas from "./sobreposicoes/Etiquetas.jsx";
import ConferirDocumentos from "./sobreposicoes/ConferirDocumentos.jsx";
import ModalDelegar from "./sobreposicoes/ModalDelegar.jsx";
import ModalTransferir from "./sobreposicoes/ModalTransferir.jsx";
import CaixaAvisos from "./sobreposicoes/CaixaAvisos.jsx";
import Encaminhar from "./sobreposicoes/Encaminhar.jsx";
import PedirAoAssistente from "./sobreposicoes/PedirAoAssistente.jsx";
import Recado from "./sobreposicoes/Recado.jsx";
import FaixaCanal from "./sobreposicoes/FaixaCanal.jsx";
import Demonstracao from "./demonstracao/Demonstracao.jsx";
import Paleta, { anotarRecente, lugarParaRecente } from "./sobreposicoes/Paleta.jsx";
import { Visor } from "./componentes/Anexo.jsx";
import AvisoDoWhatsapp from "./componentes/AvisoDoWhatsapp.jsx";

/**
 * A casca da mesa.
 *
 * Três colunas no computador (trilho, lista, conteúdo) e uma de cada vez no
 * celular. No computador não há faixa em cima: a casca inteira — marca,
 * escritório, paleta, os onze lugares, sino e conta — é o trilho, que abre
 * sozinho quando o ponteiro chega perto e flutua por cima em vez de empurrar.
 *
 * A coluna de conversas é da tela de Atendimentos e só dela — em Fluxos ou
 * Ajustes ela não conduz a nada, porque ali o que se mexe é regra do
 * escritório, não caso de cliente.
 *
 * O painel da direita empurra quando há espaço e flutua quando não há; é a
 * única decisão de layout que muda o resto, então mora aqui.
 *
 * As três colunas continuam um flex, e não o `Resizable` do shadcn: aqui a
 * largura de cada uma é CONSEQUÊNCIA da janela (`empurraPainel`,
 * `listaEscondida`), e duas das três colunas nem sempre existem. Um grupo de
 * painéis arrastáveis guarda a largura que a pessoa escolheu — e passaria a
 * discordar da regra que esconde a lista abaixo de 1180px, além de pôr divisores
 * onde no celular só cabe uma coluna.
 */
/*
 * A preferência de coluna recolhida, no navegador.
 *
 * `try` nos dois porque `localStorage` ATIRA em contexto sem site data (janela
 * privada, navegador configurado para bloquear, captura de miniatura) — e a mesa
 * não pode deixar de abrir por causa de uma preferência de largura.
 */
const CHAVE_RECOLHIDA = { lista: "mesa.lista.recolhida", ficha: "mesa.ficha.recolhida" };
function lerRecolhida(qual) {
  try {
    return localStorage.getItem(CHAVE_RECOLHIDA[qual]) === "1";
  } catch {
    return false;
  }
}
function salvarRecolhida(qual, valor) {
  try {
    localStorage.setItem(CHAVE_RECOLHIDA[qual], valor ? "1" : "0");
  } catch {
    /* navegador sem storage: só não lembra na próxima abertura */
  }
}

/*
 * A ALÇA de 14px na borda MORREU (13/09).
 *
 * Ela existia porque recolher era uma porta de mão única: o botão que recolhia a
 * coluna ia embora junto com ela. Isso acabou — o interruptor do cabeçalho
 * (`InterruptorDeColuna`) existe nos dois estados e em qualquer largura.
 *
 * O que a fez sair não foi só a redundância: ela tinha o MESMO nome acessível do
 * interruptor ("Mostrar a ficha do caso"), então quem usa leitor de tela ouvia
 * dois controles idênticos sem diferença entre eles — e foi assim que a
 * conferência de tela a encontrou, com o Playwright recusando o clique por
 * ambiguidade. Dois alvos para o mesmo gesto, um deles com 14px de largura e
 * descoberto por acaso, é o contrário de fácil de usar.
 */

/* Os endereços da tela do WhatsApp do escritório, que morreu: circulam em
   mensagem antiga e caem na mesa. */
const ENDERECOS_DA_CAIXA = new Set(["/caixa", "/whatsapp"]);

export default function Casca() {
  const estado = useEstado();
  const acoes = useAcoes();
  const nav = useNavegacao();
  const celular = useCelular();
  const atual = useCasoAtual();

  /*
   * Endereço que morreu vira `/atendimentos` AQUI, e não só no `<Navigate>` de
   * `rotas.jsx`. No celular, a vista de atendimentos é só a lista, e a casca não
   * monta o `<Outlet>` — onde o `<Navigate>` mora. O link antigo abria a lista
   * certa com o endereço errado na barra, e o botão voltar levava de novo a ele.
   */
  const { pathname, search } = useLocation();
  const navegar = useNavigate();
  useEffect(() => {
    if (ENDERECOS_DA_CAIXA.has(pathname)) navegar(`/atendimentos${search}`, { replace: true });
  }, [pathname, search, navegar]);

  /* O trilho abre sozinho quando o ponteiro chega perto — isso é assunto dele.
     O que a casca decide é se ele fica FIXADO, porque só isso muda o resto: o
     trilho fixado ocupa 246px em vez de 56, e esses 190px saem da conversa.
     Fixar só na tela larga: abaixo de 1180px a lista já foi sacrificada, e
     espiar (que flutua por cima) continua valendo em qualquer largura. */
  const [querTrilho, setQuerTrilho] = useState(lerTrilhoAberto);
  const podeFixarTrilho = useJanela((j) => !celular && j.largura >= LARGURA_MINIMA_PARA_ABRIR);
  const trilhoFixado = querTrilho && podeFixarTrilho;
  const alternarTrilho = useCallback(() => {
    setQuerTrilho((antes) => {
      salvarTrilhoAberto(!antes);
      return !antes;
    });
  }, []);

  /*
   * As duas colunas RECOLHEM, e a preferência é de quem olha.
   *
   * Em desktop com zoom alto (1920 a 200% são 960px de CSS) a mesa fica com
   * trilho + lista de 380 + ficha de 330, e a conversa recebe o que sobra —
   * bolha de duas palavras por linha, e ninguém suspeita da tela: ela só fica
   * ruim. Recolher devolve a largura a quem está lendo, e reabre pela alça na
   * borda da coluna.
   *
   * `localStorage` e não a URL, pela mesma razão do detalhe do filtro da mesa:
   * é preferência de quem olha, não recorte — um link com "a ficha recolhida"
   * dentro chegaria ao colega mudando a tela dele. (`?painel=0` continua com o
   * trabalho dele: fechar a ficha que FLUTUA, onde ela cobre a conversa.)
   */
  const [listaRecolhida, setListaRecolhida] = useState(() => lerRecolhida("lista"));
  const [painelRecolhido, setPainelRecolhido] = useState(() => lerRecolhida("ficha"));
  const recolher = useCallback((qual, valor) => {
    salvarRecolhida(qual, valor);
    (qual === "lista" ? setListaRecolhida : setPainelRecolhido)(valor);
  }, []);

  /*
   * A paleta (Ctrl+K / ⌘K) mora aqui porque é da mesa inteira, e o atalho tem de
   * valer em qualquer vista.
   *
   * `preventDefault` no combo: no Firefox ⌘K/Ctrl+K é "buscar na web" e roubaria
   * a tecla. Escrita em campo NÃO é exceção — ⌘K dentro do compositor continua
   * abrindo a paleta, porque é o atalho de navegar, não de escrever; o que se
   * escreveu fica onde está (o rascunho é da redação, e ninguém o sobrescreve).
   */
  const [paletaAberta, setPaletaAberta] = useState(false);
  useEffect(() => {
    const tecla = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletaAberta((antes) => !antes);
      }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, []);

  /*
   * As medidas da casca, escritas no `<html>`.
   *
   * São três: a altura da faixa de cima (que no computador não existe mais), a
   * largura que o trilho reserva e o quanto o rodapé ocupa. Quem precisa delas
   * FLUTUA — a faixa de "reconectando" e o balão do sino —, e o balão sai por
   * portal no fim do `<body>`: variável escrita na casca não chega até lá.
   * Número repetido em três arquivos é o número que envelhece só num deles.
   */
  useEffect(() => {
    const medidas = celular
      ? {
          /* A faixa de cima cresce pelo entalhe da tela (`viewport-fit=cover`), e
             o rodapé pela barra de gestos: quem flutua tem de saber a medida de
             AGORA, não a de um iPhone sem entalhe. */
          "--barra-da-mesa": "calc(38px + env(safe-area-inset-top))",
          "--trilho": "0px",
          /* Zero dentro da conversa: lá a barra de abas não existe, e quem
             flutua (a faixa de "reconectando") pousaria 58px acima do nada. */
          "--rodape": nav.casoId ? "env(safe-area-inset-bottom)" : "calc(58px + env(safe-area-inset-bottom))",
        }
      : {
          "--barra-da-mesa": "0px",
          /* O balão do sino segura o trilho aberto (ver `Trilho`), então a
             medida que ele lê tem de ser a de agora — encostado em 56 com o
             trilho aberto em 246, o balão nasceria por baixo da coluna. */
          "--trilho": `${trilhoFixado || nav.avisosAbertos ? TRILHO_ABERTO : TRILHO_FECHADO}px`,
          /* Zero: no computador não há barra embaixo, e quem flutua (o recado)
             já traz o próprio respiro. */
          "--rodape": "0px",
        };
    for (const [nome, valor] of Object.entries(medidas)) document.documentElement.style.setProperty(nome, valor);
  }, [celular, trilhoFixado, nav.avisosAbertos, nav.casoId]);

  /*
   * A altura de VERDADE, quando o teclado do telefone está aberto.
   *
   * `100dvh` já resolve a barra do navegador que entra e sai, mas não o teclado:
   * no iOS ele não encolhe a viewport de layout — ele desliza a página inteira
   * para cima, e o campo de escrever a mensagem sai por baixo dele. O
   * `visualViewport` é a única medida que sabe o que está REALMENTE visível.
   *
   * O `scrollTo(0,0)` no mesmo evento é a segunda metade do defeito: focar um
   * campo perto do rodapé faz o Safari rolar o documento (mesmo com
   * `overflow:hidden`), e a faixa de cima da mesa some sem que nada consiga
   * trazê-la de volta.
   */
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const medir = () => {
      document.documentElement.style.setProperty("--altura-viva", `${Math.round(vv.height)}px`);
      window.scrollTo(0, 0);
    };
    medir();
    vv.addEventListener("resize", medir);
    vv.addEventListener("scroll", medir);
    return () => {
      vv.removeEventListener("resize", medir);
      vv.removeEventListener("scroll", medir);
    };
  }, []);

  /*
   * O nome da aba é o LUGAR.
   *
   * O título era fixo no `index.html`, então três clientes abertos em três abas
   * davam três abas idênticas — e dez entradas de mesmo nome no histórico. Como a
   * mesa promete "manda o link desse atendimento", a aba tem de dizer de quem é.
   */
  /* Quem acabou de trocar a senha provisória chega pelos primeiros passos — uma
     vez: a boas-vindas diz em que setor a pessoa está e o que falta, e o
     endereço da mesa não é o lugar dessa conversa. */
  const { recemChegado } = estado;
  const { esquecerChegada } = acoes;
  const { ir } = nav;
  useEffect(() => {
    if (!recemChegado || !estado.usuario) return;
    esquecerChegada();
    ir("/comecar");
  }, [recemChegado, estado.usuario, esquecerChegada, ir]);

  const nomeDoLugar = estado.usuario ? (atual?.nome ?? TITULOS[nav.vista] ?? "Mesa de atendimento") : "";
  useEffect(() => {
    /* Sem sessão fica o título do `index.html`: a tela de entrar não é lugar da
       mesa, e anunciar "Atendimentos" antes do login é mentira. */
    if (nomeDoLugar) document.title = `${nomeDoLugar} — Galera Advocacia`;
  }, [nomeDoLugar]);

  /*
   * Onde a pessoa esteve, para a paleta abrir já mostrando o caminho de volta.
   *
   * Anotado aqui e não na paleta: a paleta só existe aberta — se ela guardasse
   * o rastro, o rastro começaria no primeiro Ctrl+K. O nome do atendimento entra
   * quando ele chega (`atual`), porque o recente "Atendimento #12" não diz nada
   * a quem lê a lista amanhã.
   */
  useEffect(() => {
    if (!estado.usuario) return;
    const lugar = lugarParaRecente({
      vista: nav.vista,
      casoId: nav.casoId,
      nomeDoCaso: atual?.nome,
      caminho: nav.caminho,
    });
    if (lugar) anotarRecente(lugar);
  }, [estado.usuario, nav.vista, nav.casoId, nav.caminho, atual?.nome]);

  const emAtendimentos = nav.vista === "chats";
  const focoNoChat = Boolean(nav.casoId);
  /*
   * Onde cabe, a ficha fica SEMPRE aberta.
   *
   * Era um interruptor no topo da conversa, e o padrão de quem fechava uma vez
   * passava a ser "conversa sem ficha" — a coluna que tem o próximo passo, as
   * pendências e quem cuida do caso ficava escondida atrás de um clique que
   * ninguém lembrava de dar. `?painel=0` deixa de fechar onde a coluna EMPURRA:
   * a partir de 880px ela é parte da tela, não uma gaveta.
   *
   * Pela janela e só com os booleanos: a casca é a raiz da mesa, e lendo a
   * largura crua ela redesenhava tudo a cada pixel de arrasto. Assim redesenha
   * quando uma coluna entra ou sai. Fica antes dos retornos abaixo: é gancho.
   */
  const { empurraPainel, cabeEmpurrar, mostrarLista, mostrarConteudo, listaEscondida } = useJanela((j) => {
    const colunas = colunasDaMesa({
      largura: j.largura,
      emAtendimentos,
      focoNoChat,
      trilhoFixado,
      listaRecolhida,
      painelRecolhido,
    });
    return {
      empurraPainel: colunas.empurraPainel,
      cabeEmpurrar: colunas.cabeEmpurrar,
      mostrarLista: colunas.mostrarLista,
      mostrarConteudo: colunas.mostrarConteudo,
      listaEscondida: colunas.listaEscondida,
    };
  });

  if (!estado.pronto) return <Abertura />;
  /* Senha provisória antes de tudo: a portaria não abre o escritório até a troca. */
  if (estado.trocaDeSenha) return <TrocaDeSenha />;
  if (!estado.usuario) return <Entrada />;

  /* O caso da URL não abriu (não existe, é de outro escritório, o servidor não
     respondeu). O foco continua no chat — é lá que a explicação aparece —, mas
     o que só faz sentido COM caso sai da tela: painel de abas vazias ao lado de
     um "não encontrado", e a petição, que se limitava a devolver `null` deixando
     a mesa inteira `inert` por baixo. Isso era tela morta, sem nem o clique. */
  const casoFalhou = Boolean(estado.casoErro);
  /* Painel-folha (o que flutua por cima) só com pedido explícito: `painelAberto`
     é verdadeiro quando o parâmetro nem existe, então no celular a conversa
     abria coberta pelo Copiloto — a pessoa tocava numa conversa e não via
     conversa nenhuma. Onde ele empurra, continua nascendo aberto. */
  /*
   * Quem governa a ficha depende da LARGURA, e não dos dois ao mesmo tempo.
   *
   * Era `(empurraPainel || nav.painelPedido)`, um OU entre os dois mecanismos —
   * e o OU é que fazia a ficha voltar por cima da conversa numa tela de 1920px:
   * com ela recolhida, `empurraPainel` é falso, o `?painel=1` do endereço
   * respondia pelo outro lado do OU, e ela aparecia… como GAVETA, porque o
   * leiaute também pergunta `empurraPainel`. Aberta por cima do atendimento, com
   * véu, numa largura em que ela é coluna.
   *
   * Onde ela CABE como coluna, quem manda é a escolha guardada
   * (`painelRecolhido`); onde ela flutua, quem manda é o endereço. Um ou outro,
   * nunca os dois.
   */
  const mostrarPainel =
    nav.painelAberto && emAtendimentos && focoNoChat && !casoFalhou && (cabeEmpurrar ? empurraPainel : nav.painelPedido);

  /*
   * ALTERNAR a ficha — uma função só, e a casca é quem sabe COMO.
   *
   * São dois mecanismos conforme a largura. Onde ela empurra (≥880px) a ficha é
   * coluna da mesa e esconder é `painelRecolhido`, estado da casca guardado no
   * navegador. Onde ela flutua, é `?painel=` — endereço, porque a gaveta aberta
   * faz parte do link que se manda por mensagem.
   *
   * Uma função porque DOIS lugares a chamam: o interruptor do cabeçalho e o véu
   * atrás da gaveta. Escrita duas vezes, o véu fechava pelo caminho errado —
   * marcava `painelRecolhido` numa ficha flutuante, que não olha essa chave, e o
   * toque fora simplesmente não fazia nada.
   */
  /* Função simples e não `useCallback`: este ponto do componente fica DEPOIS dos
     retornos de `Abertura`/`Entrada`, e gancho atrás de retorno condicional muda
     a ordem dos ganchos entre renders. O objeto de contexto do `Outlet` já nasce
     novo a cada render, então não há memo a preservar. */
  const alternarPainel = () => {
    /* `cabeEmpurrar` e NÃO `empurraPainel`: a pergunta é "nesta largura a ficha é
       coluna?", e não "ela está aberta agora?" — ver `colunasDaMesa`. */
    if (cabeEmpurrar) return recolher("ficha", mostrarPainel);
    nav.ajustar({
      painel: mostrarPainel ? "0" : !nav.secaoDoPainel || nav.secaoDoPainel === "0" ? "1" : nav.secaoDoPainel,
    });
  };
  /*
   * A petição cobre a mesa inteira, e o que está atrás sai do alcance.
   *
   * `inert` é a peça nativa disso: o que está por baixo deixa de receber foco e
   * sai da árvore de acessibilidade — sem isso, quem chega pelo teclado saía do
   * editor da peça e caía nos botões da trilha ESCONDIDOS sob uma superfície
   * opaca. É `inert` e não a cortina do Headless UI de propósito: o motor de
   * .docx abre menus por portal em `document.body`, e um foco preso os
   * quebraria.
   */
  const peticaoNaFrente = nav.peticaoAberta && focoNoChat && !casoFalhou;

  return (
    /*
     * Uma faixa em cima, as colunas embaixo.
     *
     * A faixa é a casca inteira — marca, escritório, as onze vistas, sino e
     * conta — e atravessa a mesa porque o que ela carrega vale para as onze
     * telas. Presa numa das colunas ela sumiria justamente onde a coluna não
     * existe (o celular não tem trilho, e Fluxos não tem lista). Navegar não é
     * trabalho dela: isso é o `Trilho`, na lateral, onde ícone se acha pela
     * posição.
     */
    <div className="relative flex h-[var(--altura-viva,100dvh)] w-full flex-col overflow-hidden bg-background">
      {/*
       * O ATALHO DE PULAR, primeiro de tudo — o "skip to content" da mesa.
       *
       * Ele morava dentro da conversa (`Conversa.jsx`), o que o tornava o
       * primeiro daquela coluna e o 44º da página: 14 paradas no trilho, 19 na
       * barra de filtro da lista (os rostos da equipe, as oito etapas do funil,
       * o contador, o "etapas"), 3 nos controles da coluna e 8 nas conversas. Um
       * atalho que só se alcança depois de varrer a mesa inteira não é atalho —
       * e quem responde cliente de teclado o dia inteiro pagava isso em cada
       * conversa que abria.
       *
       * Não é montado com a petição na frente: ali o campo de mensagem está sob
       * a árvore `inert`, e o atalho levaria o foco para o que não recebe foco.
       * Invisível até receber o foco (`.pular`, em `acesso.css`).
       */}
      {emAtendimentos && focoNoChat && !casoFalhou && !peticaoNaFrente ? (
        <a
          href="#campo-da-mensagem"
          className="pular"
          /* Foca no código em vez de deixar o navegador pendurar
             `#campo-da-mensagem` na URL: o endereço desta tela é link que se
             manda por mensagem. */
          onClick={(e) => {
            e.preventDefault();
            document.getElementById("campo-da-mensagem")?.focus();
          }}
        >
          Pular para o campo de mensagem
        </a>
      ) : null}
      {/* `display:contents` para o `inert` não mexer no layout: os filhos seguem
          sendo a barra e a fila de colunas da mesa. */}
      <div className="contents" inert={peticaoNaFrente}>
        {/* A faixa de cima é só do telefone: no computador a casca inteira é o
            trilho, e 38px de altura em todas as onze telas voltaram para a
            conversa. */}
        {celular && <BarraSuperior aoAbrirPaleta={() => setPaletaAberta(true)} />}
        {/* O canal fora do ar é notícia do ESCRITÓRIO, e por isso atravessa a
            mesa inteira — acima do trilho, da lista, da conversa e da ficha.
            Dentro da coluna de conteúdo (onde nasceu) ela dividia a faixa com a
            ficha ao lado: num tablet de 760px o motivo quebrava numa palavra por
            linha e empurrava a conversa 290px para baixo. Só existe com o canal
            fora. */}
        <AvisoDoWhatsapp />
        {/* `relative`: é este retângulo que a ficha FLUTUANTE usa como moldura
            (`absolute inset-y-0 right-0`). Sem ele ela se ancorava na casca
            inteira e passava por cima da faixa de aviso, que fica acima das
            colunas. */}
        <div className={`relative flex min-h-0 min-w-0 flex-1 ${celular ? "flex-col" : "flex-row"}`}>
          {!celular && (
            <Trilho
              fixado={trilhoFixado}
              podeFixar={podeFixarTrilho}
              aoFixar={alternarTrilho}
              aoAbrirPaleta={() => setPaletaAberta(true)}
            />
          )}
          {mostrarLista && <Lista />}
          {mostrarConteudo && (
            <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
              {/* `painelNaTela` sai daqui e não do cabeçalho: a regra de quando o
                painel aparece é uma só, e o botão "Painel" precisa dela para não
                dizer "Fechar painel" sobre um painel que não está na tela.

                `listaNaTela` desce pelo MESMO motivo, e conserta uma zona morta:
                entre 880 e 1179px a lista sai da tela (`listaEscondida`) sem que
                a janela seja celular, então o ← do cabeçalho — que perguntava
                `useCelular()` — não existia e a pessoa ficava presa na conversa,
                sem caminho de volta e sem `<h1>` na página. A largura da lista em
                `Conversa.jsx` calculava a mesma regra pela segunda vez: duas
                contas da mesma coisa é a que envelhece só num dos arquivos. */}
              {/* Trocar de lugar tem de PARECER trocar de lugar: sem isso a mesa
                inteira se substituía de um quadro para o outro, e a diferença
                entre "Fluxos" e "Ajustes" era o texto do título. A chave é a
                vista, não a URL: abrir outro atendimento dentro de Atendimentos
                não é mudança de lugar — e reanimar ali piscava a conversa a cada
                clique na lista. `flex:1` e `min-height:0` porque este div passou
                a existir no meio de uma coluna flex que rola. */}
              <div
                key={nav.vista}
                className="flex min-h-0 min-w-0 flex-1 flex-col animate-[vFade_var(--t-media)_var(--e-entra)_both]"
              >
                {/* A vista chega em pacote próprio (ver `rotas.jsx`), então há um
                    vão entre o clique e a tela. O esqueleto nasce INVISÍVEL e só
                    aparece depois de 0,18s de espera: o pacote local costuma
                    chegar em menos que isso, e um esqueleto que pisca por dois
                    quadros é mais barulhento que a espera que ele explica. É
                    atraso de animação, não temporizador — nada de JavaScript
                    para decidir isso. */}
                <Suspense fallback={<VistaChegando />}>
                  <Outlet
                    context={{
                      empurraPainel,
                      painelNaTela: mostrarPainel,
                      listaNaTela: mostrarLista,
                      /*
                       * ALTERNAR a lista, nos dois sentidos — e não só recolher.
                       *
                       * Era `aoRecolherLista`, entregue apenas com a lista NA
                       * TELA: recolhida, o botão sumia junto com ela e o caminho
                       * de volta era a alça de 14px na borda, que é um alvo que
                       * se descobre por acaso. Esconder e mostrar é o mesmo
                       * gesto, então é o mesmo botão, sempre no mesmo lugar.
                       *
                       * Some onde a lista NÃO PODE estar na tela: no celular ela
                       * é a própria tela, e entre 880 e 1179px a ficha empurra e
                       * não há largura para as três colunas (`listaEscondida`) —
                       * ali quem devolve a lista é recolher a FICHA, que tem
                       * botão próprio ao lado. Botão que não muda nada é a tela
                       * mentindo.
                       */
                      aoAlternarLista:
                        celular || !focoNoChat
                          ? undefined
                          : () => {
                              if (mostrarLista) return recolher("lista", true);
                              /*
                               * Pedir a lista onde ela NÃO CABE abre espaço
                               * fechando a ficha, em vez de o botão sumir.
                               *
                               * Entre 880 e 1179px as três colunas não cabem e a
                               * lista é a sacrificada (`listaEscondida`). O botão
                               * então desaparecia — num notebook de 1366 com a
                               * janela pela metade não havia como trazer a lista
                               * de volta, e o controle que some é pior que o
                               * controle que precisa negociar espaço. "Mostra a
                               * lista" passa a fazer exatamente isso.
                               */
                              if (listaEscondida) recolher("ficha", true);
                              recolher("lista", false);
                            },
                      aoAlternarPainel: alternarPainel,
                    }}
                  />
                </Suspense>
              </div>
            </div>
          )}
          {/* SEM `aoRecolher`: quem esconde a ficha é o interruptor do cabeçalho
              da conversa, que agora existe em qualquer largura. O ✕ dentro do
              painel era um terceiro caminho para o mesmo gesto — com a alça na
              borda, eram três controles com três desenhos para "esconder esta
              coluna", cada um num canto diferente da tela. */}
          {/* O VÉU atrás da ficha flutuante.
              Sem ele a coluna paira sobre uma conversa que continua clicável, e
              "como eu fecho isto" vira caçar o botão — o toque fora é o gesto
              que todo mundo tenta primeiro numa gaveta. Só onde ela FLUTUA: onde
              empurra, ela é coluna da mesa e não tem o que velar. */}
          {mostrarPainel && !empurraPainel && (
            <button
              type="button"
              /* Nome PRÓPRIO, e não o do interruptor: dois controles com o
                 mesmo nome acessível é o defeito que tirou a alça daqui, e o véu
                 tinha acabado de repeti-lo. Para quem usa leitor de tela, este é
                 "fechar tocando fora" — um gesto diferente, com nome diferente. */
              aria-label="Fechar a ficha do caso tocando fora dela"
              /* Marca para a conferência de tela: o véu só pode existir onde a
                 ficha FLUTUA — vê-lo em tela larga é a ficha abrindo por cima do
                 atendimento, que é o defeito que `cabeEmpurrar` apagou. */
              data-veu="ficha"
              onClick={alternarPainel}
              className="absolute inset-0 z-[49] cursor-default border-none bg-foreground/25 animate-[vFade_var(--t-curta)_var(--e-entra)_both]"
            />
          )}
          {mostrarPainel && <Painel empurra={empurraPainel} />}
          {/* Dentro de uma conversa a barra de abas SAI, como em todo aplicativo
              de mensagem: 58px de navegação embaixo de um teclado aberto empurra
              a conversa para uma faixa de três linhas, e a volta já é o ← do
              cabeçalho. Fora dela a barra é a única navegação que existe. */}
          {celular && !focoNoChat && <BarraInferior />}
        </div>
      </div>

      {peticaoNaFrente && <TelaPeticao />}
      {nav.conferirAberto && focoNoChat && !casoFalhou && <ConferirDocumentos />}
      {nav.delegarAberto && focoNoChat && !casoFalhou && <ModalDelegar />}
      {nav.avisosAbertos && <CaixaAvisos />}
      {nav.transferirAberto && focoNoChat && !casoFalhou && <ModalTransferir />}
      {/* Etiquetas NÃO exige `focoNoChat`: ela carrega o id do caso no endereço
          justamente porque abre pelo menu de contexto de uma linha da lista, com
          a conversa fechada. As outras quatro valem para o caso aberto. */}
      {nav.etiquetasDe ? <Etiquetas /> : null}
      {/* O visor de anexo é um só para a tela inteira: a bolha e a aba de
          anexos abrem o mesmo, com a lista completa da conversa. */}
      {/* Montada só quando abre: é o que faz ela nascer limpa, sem o termo da
          pergunta anterior nem o resultado dela. */}
      {paletaAberta && <Paleta aoFechar={() => setPaletaAberta(false)} />}
      <Encaminhar />
      {/* Montado só quando há pedido: assim ele nasce com o campo limpo, sem o
          texto do pedido anterior. */}
      {estado.pedindoAoAssistente?.length ? (
        <PedirAoAssistente mensagens={estado.pedindoAoAssistente} aoFechar={acoes.fecharPedidoAoAssistente} />
      ) : null}
      <Visor />
      <FaixaCanal />
      <Recado />
      {/* A demonstração guiada: um ponteiro e um HUD para a mesa inteira, e
          por isso mora aqui e não dentro de uma vista. */}
      <Demonstracao />
    </div>
  );
}

/** O lugar de uma vista enquanto o pacote dela chega. */
function VistaChegando() {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-3 p-[18px_19px] animate-[vFade_var(--t-media)_var(--e-entra)_.18s_both]"
      aria-busy="true"
    >
      <Esqueleto linhas={4} altura={78} raio={13} rotulo="Abrindo a tela…" />
    </div>
  );
}

/**
 * A abertura da mesa.
 *
 * A primeira impressão do sistema não pode ser um retângulo areia vazio: com o
 * servidor recém-subido (tsx acordando, banco abrindo) `/api/auth/eu` leva
 * segundos, e nada na tela não se distingue de tela quebrada.
 *
 * Quem abre a mesa vê a MARCA — a mesma da entrada, e não uma inicial
 * desenhada com um `<span>` de serifa. O traço que atravessa é indeterminado
 * porque a espera é indeterminada; barra que finge saber a porcentagem mente
 * duas vezes.
 */
function Abertura() {
  /*
   * Passados alguns segundos a espera deixa de ser normal e vira dúvida
   * ("travou? fui deslogado?"). O segundo recado não é erro — é o motivo real,
   * e ele só aparece quando a demora já foi sentida: dizer "o servidor está
   * acordando" no primeiro quadro de uma abertura de 300ms é ruído.
   */
  const [demorando, setDemorando] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDemorando(true), 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    /* `role="status"`: para o leitor de tela isto é uma espera anunciada, e não
       uma tela em branco entre o login e a mesa. */
    <div role="status" aria-live="polite" className="flex h-dvh flex-col items-center justify-center gap-0 bg-background px-6">
      {/* A marca navy na mesa clara, a branca no escuro — as duas já servem o
          favicon do `index.html`. */}
      <img
        src="/fga-mark.png"
        alt="Galera Advocacia"
        className="block h-[52px] w-auto animate-[vRespira_2.6s_var(--e-entra)_infinite] dark:hidden"
      />
      <img
        src="/fga-mark-branca.png"
        alt="Galera Advocacia"
        className="hidden h-[52px] w-auto animate-[vRespira_2.6s_var(--e-entra)_infinite] dark:block"
      />
      <div className="mt-[18px] font-serif text-[22px] leading-none">Mesa de atendimento</div>
      {/* O trilho: 3px de altura para ser movimento na periferia da vista, não
          um elemento a mais para ler. */}
      <div className="mt-[22px] h-[3px] w-[148px] overflow-hidden rounded-full bg-border">
        <div className="h-full w-[42%] rounded-full bg-primary animate-[vAbrindo_1.5s_var(--e-entra)_infinite]" />
      </div>
      <div className="mt-3 text-[12.5px] text-muted-foreground">Abrindo a mesa…</div>
      {/* Altura reservada: o recado aparecendo não pode empurrar a marca para
          cima no meio da espera. */}
      <div className="mt-1.5 h-4 text-[11.5px] text-muted-foreground/80">
        {demorando ? (
          <span className="animate-[vFade_.3s_var(--e-entra)]">O servidor está acordando — só mais um instante.</span>
        ) : null}
      </div>
    </div>
  );
}
